/*
 * Copyright (C) 2013-2026 Neckar IT GmbH, Mössingen, Germany
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Linking this library statically or dynamically with other modules is
 * making a combined work based on this library. Thus, the terms and
 * conditions of the GNU General Public License cover the whole combination.
 *
 * As a special exception, the copyright holders of this library give you
 * permission to link this library with independent modules, regardless of
 * the license terms of these independent modules, and to copy and distribute
 * the resulting combined work under terms of your choice, provided that every
 * copy of the combined work is accompanied by a complete copy of the source
 * code of this library.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
package it.neckar.open.kotlin.lang

import com.meistercharts.annotations.Domain
import it.neckar.open.collections.fastForEach
import it.neckar.open.unit.other.pct

/**
 *
 */


inline fun count(cond: (index: Int) -> Boolean): Int {
  var counter = 0
  while (cond(counter)) counter++
  return counter
}

inline fun <reified T> mapWhile(cond: (index: Int) -> Boolean, gen: (Int) -> T): List<T> = arrayListOf<T>().apply { while (cond(this.size)) this += gen(this.size) }
inline fun <reified T> mapWhileArray(cond: (index: Int) -> Boolean, gen: (Int) -> T): Array<T> = mapWhile(cond, gen).toTypedArray()

fun <T> List<T>.getCyclic(index: Int) = this[index umod this.size]
fun <T> Array<T>.getCyclic(index: Int) = this[index umod this.size]


/**
 * Returns the n th element from the list. Uses modulo if the index is larger than the size of the list
 */
fun <T> List<T>.getModulo(index: Int): T {
  require(isNotEmpty()) { "List must not be empty" }

  //This calculation produces a "wrap around" effect for negative indices
  return this[index.wrapAround(size)]
}

/**
 * Returns the n th element from the list. Uses modulo if the index is larger than the size of the list.
 * Returns null if the list is empty.
 */
fun <T> List<T>.getModuloOrNull(index: Int): T? {
  //This calculation produces a "wrap around" effect for negative indices
  return if (isEmpty()) null else this[index.wrapAround(size)]
}

fun <T> List<T>.getModuloOrElse(index: Int, fallback: T): T {
  //This calculation produces a "wrap around" effect for negative indices
  return if (isEmpty()) fallback else this[index.wrapAround(size)]
}

/**
 * Iterates over this collection delivering its elements to [consumer] while the [consumer] returns true for a consumed element
 * @see [consumeUntil]
 */
inline fun <E> Collection<E>.consumeWhile(consumer: (E) -> Boolean) {
  val iter = iterator()
  while (iter.hasNext()) {
    if (!consumer(iter.next())) {
      return
    }
  }
}

/**
 * Iterates over this collection delivering its elements to [consumer] until the [consumer] returns false for a consumed element
 * @see [consumeWhile]
 * @return true if the consumer returned true for any value. false otherwise
 */
inline fun <E> Collection<E>.consumeUntil(consumer: (E) -> Boolean): Boolean {
  val iter = iterator()
  while (iter.hasNext()) {
    if (consumer(iter.next())) {
      return true
    }
  }

  return false
}

/**
 * Consumes the consumer until it returns the cancel value
 * @return the cancel value or null if no consumer has returned the cancel value
 */
inline fun <E, T> Collection<E>.consumeUntil(cancelValue: T, consumer: (E) -> T): T? {
  val iter = iterator()
  while (iter.hasNext()) {
    if (consumer(iter.next()) == cancelValue) {
      return cancelValue
    }
  }

  return null
}

/**
 * Returns a list of values that have relative values (the sum of all values is equal to 1.0).
 *
 * Requires the receiver to be non-empty and have a non-zero sum.
 */
fun List<@Domain Double>.toRelativeValues(): List<@pct Double> {
  @Domain val sum = this.sum()
  require(sum != 0.0) { "Cannot compute relative values for a list with sum 0.0 (size=$size)" }
  return map {
    1 / sum * it
  }
}

/**
 * Removes elements from the list until the max size has been reached
 */
fun <T> MutableList<T>.deleteFromStartUntilMaxSize(maxSize: Int) {
  require(maxSize >= 0) { "Invalid max size: $maxSize" }

  while (this.size > maxSize) {
    removeAt(0)
  }
}

/**
 * Adds the element, and removes elements from the start until the max size has been reached
 */
fun <T> MutableList<T>.addWithMaxSize(element: T, limit: Int) {
  add(element)
  while (size > limit) {
    removeAt(0)
  }
}

/**
 * Sets  the element as last element. Replaces the last element!
 */
fun <E> MutableList<E>.setLast(element: E) {
  this[lastIndex] = element
}

/**
 * Returns a new list containing:
 * * null
 * * all elements from this
 */
fun <E> List<E>.withNullAtFirst(): List<E?> {
  return buildList {
    add(null)
    addAll(this@withNullAtFirst)
  }
}

/**
 * Returns a new list with the given elements at the given position (set)
 */
fun <E> List<E>.withElementAt(index: Int, element: E): List<E> {
  return buildList {
    addAll(this@withElementAt)
    this[index] = element
  }
}

fun <E> LinkedHashSet<E>.removeFirst() {
  remove(first())
}


/**
 * Filters the collection by the given filters.
 */
fun <T> Collection<T>.filterAll(filterMatchers: List<(T) -> Boolean>): Collection<T> {
  if (filterMatchers.isEmpty()) {
    return this
  }

  return filter { element ->
    filterMatchers.fastForEach { matcher ->
      if (matcher(element).not()) {
        return@filter false
      }
    }

    return@filter true
  }
}

/**
 * Joins at most [maxReported] elements and counts the rest, for messages a human has to read.
 *
 * A message that lists every element of a large collection is one nobody reads — and an error message
 * exists to be read. [noun] names what is being counted, in singular; the plural adds an `s`.
 *
 * ```kotlin
 * listOf("a", "b", "c").abbreviated(maxReported = 2, noun = "value")  // "a, b, and 1 more value"
 * ```
 */
fun Iterable<String>.abbreviated(maxReported: Int, noun: String): String {
  require(maxReported > 0) { "maxReported must be positive but was $maxReported" }

  val elements: List<String> = toList()
  if (elements.size <= maxReported) return elements.joinToString(", ")

  val remaining = elements.size - maxReported
  val plural = if (remaining == 1) "" else "s"

  return elements.take(maxReported).joinToString(", ") + ", and $remaining more $noun$plural"
}
